import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FeedTokenService, FeedToken } from '@/services/feedTokenService';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { api } from '@/services/api';
import { IsolatedContent } from '@/components/IsolatedContent';
import { CompanyPicker } from 'shell/CompanyPicker';
import {
  Loader2,
  AlertCircle,
  Rss,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';

interface UserProfile {
  id: string;
  companyId?: string;
}

interface FeedItem {
  title: string;
  description: string;
  pubDate: string;
  link: string;
  content: string;
}

export const FeedViewer: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Company selection state
  const { userCompanies, getCompanyColor } = useUserCompanies();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(() => {
    // Restore from localStorage on mount
    return localStorage.getItem('articleCreator_selectedCompanyId');
  });

  // Feed state
  const [feedTokens, setFeedTokens] = useState<FeedToken[]>([]);
  const [feedItems, setFeedItems] = useState<FeedItem[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);

  // Get the active company ID
  const activeCompanyId = selectedCompanyId || companyId;

  // Fetch user's default company on mount
  useEffect(() => {
    const fetchUserCompany = async () => {
      try {
        const response = await api.get<UserProfile>('/users/me');
        if (response.success && response.data?.companyId) {
          setCompanyId(response.data.companyId);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserCompany();
  }, []);

  // Validate stored company and auto-select if needed
  useEffect(() => {
    if (userCompanies.length === 0) return;

    // If we have a stored selection, validate it's still accessible
    if (selectedCompanyId) {
      const isValid = userCompanies.some(c => c.id === selectedCompanyId);
      if (!isValid) {
        // Stored company no longer valid, clear it
        localStorage.removeItem('articleCreator_selectedCompanyId');
        setSelectedCompanyId(null);
      }
    }

    // Auto-select if only one company and nothing selected
    if (userCompanies.length === 1 && !selectedCompanyId && !companyId) {
      setSelectedCompanyId(userCompanies[0].id);
    }
  }, [userCompanies, selectedCompanyId, companyId]);

  // Load feed tokens when company changes
  useEffect(() => {
    if (activeCompanyId) {
      loadFeedTokens(activeCompanyId);
    }
  }, [activeCompanyId]);

  const loadFeedTokens = async (targetCompanyId: string) => {
    try {
      setFeedLoading(true);
      setError(null);
      const tokens = await FeedTokenService.getTokens(targetCompanyId);
      setFeedTokens(tokens);

      if (tokens.length > 0) {
        await loadFeed(tokens[0].feedUrl);
      } else {
        setFeedItems([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feed tokens');
    } finally {
      setFeedLoading(false);
    }
  };

  const loadFeed = async (feedUrl: string) => {
    try {
      setFeedLoading(true);
      setError(null);

      const response = await fetch(feedUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch feed: ${response.status}`);
      }

      const xmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlText, 'text/xml');

      const items = doc.querySelectorAll('item');
      const parsedItems: FeedItem[] = [];

      items.forEach((item) => {
        const title = item.querySelector('title')?.textContent || '';
        const description = item.querySelector('description')?.textContent || '';
        const pubDate = item.querySelector('pubDate')?.textContent || '';
        const link = item.querySelector('link')?.textContent || '';

        // Get content:encoded - need to use getElementsByTagName for namespaced elements
        const contentEncoded = item.getElementsByTagName('content:encoded')[0]?.textContent || '';

        parsedItems.push({
          title,
          description,
          pubDate,
          link,
          content: contentEncoded,
        });
      });

      setFeedItems(parsedItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feed');
    } finally {
      setFeedLoading(false);
    }
  };

  const handleRefresh = () => {
    if (feedTokens.length > 0) {
      loadFeed(feedTokens[0].feedUrl);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with company selector */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Feed Viewer</h1>
          {userCompanies.length > 1 ? (
            <div className="mt-2">
              <CompanyPicker
                companies={userCompanies}
                selectedCompanyId={activeCompanyId || undefined}
                onCompanyChange={(companyId) => {
                  setSelectedCompanyId(companyId);
                  localStorage.setItem('articleCreator_selectedCompanyId', companyId);
                }}
                getCompanyColor={getCompanyColor}
                placeholder="Select company..."
              />
            </div>
          ) : (
            <p className="text-muted-foreground">
              Preview how your RSS feed content appears
            </p>
          )}
        </div>

        {/* Refresh Button */}
        {feedTokens.length > 0 && (
          <Button variant="outline" onClick={handleRefresh} disabled={feedLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${feedLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* No Feed Token State */}
      {!feedLoading && feedTokens.length === 0 && activeCompanyId && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <Rss className="h-12 w-12 mx-auto text-muted-foreground" />
              <div>
                <h3 className="text-lg font-medium">No Feed Configured</h3>
                <p className="text-muted-foreground mt-1">
                  Create a feed token in Settings to view your RSS feed.
                </p>
              </div>
              <Button asChild>
                <Link to="/settings">Go to Settings</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {feedLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Feed Items */}
      {!feedLoading && feedItems.length > 0 && (
        <div className="space-y-6">
          {feedItems.map((item, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(item.pubDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                  {item.link && (
                    <Button variant="ghost" size="sm" asChild>
                      <a href={item.link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <IsolatedContent html={item.content} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!feedLoading && feedTokens.length > 0 && feedItems.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <Rss className="h-12 w-12 mx-auto text-muted-foreground" />
              <div>
                <h3 className="text-lg font-medium">No Episodes Yet</h3>
                <p className="text-muted-foreground mt-1">
                  Publish some episodes to see them appear in your feed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FeedViewer;
